import React, { useState, useEffect } from 'react';
import { 
  DollarSign, 
  Coins, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Download,
  Filter,
  Search,
  User,
  Calendar,
  Hash,
  Eye,
  Edit3,
  Save
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContributionManager = ({ user, group, onContributionUpdate }) => {
  // States for data management
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    payment_mode: '',
    member_id: '',
    status: ''
  });

  // States for cash contribution recording
  const [showRecordCash, setShowRecordCash] = useState(false);
  const [cashContribution, setCashContribution] = useState({
    member_id: '',
    amount: '',
    currency: group?.currency || 'KES',
    cycle_number: 1,
    payment_round: 1,
    admin_notes: ''
  });

  // States for crypto contribution
  const [showProcessCrypto, setShowProcessCrypto] = useState(false);
  const [cryptoContribution, setCryptoContribution] = useState({
    amount: '',
    currency: 'USDT',
    network: 'polygon',
    transaction_hash: '',
    sender_wallet: ''
  });

  // States for status updates
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({
    new_status: '',
    reason: ''
  });

  // Feedback state
  const [feedback, setFeedback] = useState({ type: '', message: '', visible: false });

  // Get group members for dropdown
  const [groupMembers, setGroupMembers] = useState([]);

  useEffect(() => {
    fetchContributions();
    fetchGroupMembers();
  }, [group?.id, filters]);

  const showFeedback = (type, message) => {
    setFeedback({ type, message, visible: true });
    setTimeout(() => {
      setFeedback(prev => ({ ...prev, visible: false }));
    }, 5000);
  };

  const fetchContributions = async () => {
    if (!group?.id) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.payment_mode) params.append('payment_mode', filters.payment_mode);
      if (filters.member_id) params.append('member_id', filters.member_id);
      if (filters.status) params.append('status', filters.status);

      const response = await fetch(`${API}/contributions/${group.id}?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setContributions(data.contributions || []);
      } else {
        showFeedback('error', 'Erreur lors du chargement des contributions');
      }
    } catch (error) {
      console.error('Error fetching contributions:', error);
      showFeedback('error', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupMembers = async () => {
    if (!group?.members) return;
    
    try {
      const token = localStorage.getItem('token');
      const memberPromises = group.members.map(async (memberId) => {
        const response = await fetch(`${API}/users/${memberId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          return response.json();
        }
        return { id: memberId, username: 'Utilisateur inconnu' };
      });
      
      const members = await Promise.all(memberPromises);
      setGroupMembers(members);
    } catch (error) {
      console.error('Error fetching group members:', error);
    }
  };

  const handleRecordCashContribution = async () => {
    if (!cashContribution.member_id || !cashContribution.amount) {
      showFeedback('error', 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/contributions/cash/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          group_id: group.id,
          ...cashContribution,
          amount: parseFloat(cashContribution.amount)
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        showFeedback('success', 'Contribution cash enregistrée avec succès');
        setShowRecordCash(false);
        setCashContribution({
          member_id: '',
          amount: '',
          currency: group?.currency || 'KES',
          cycle_number: 1,
          payment_round: 1,
          admin_notes: ''
        });
        fetchContributions();
        if (onContributionUpdate) onContributionUpdate();
      } else {
        showFeedback('error', data.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Error recording cash contribution:', error);
      showFeedback('error', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessCryptoContribution = async () => {
    if (!cryptoContribution.amount || !cryptoContribution.transaction_hash || !cryptoContribution.sender_wallet) {
      showFeedback('error', 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/contributions/crypto/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          group_id: group.id,
          amount: parseFloat(cryptoContribution.amount),
          currency: cryptoContribution.currency,
          network: cryptoContribution.network,
          transaction_hash: cryptoContribution.transaction_hash,
          sender_wallet: cryptoContribution.sender_wallet
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        showFeedback('success', 'Contribution crypto traitée avec succès');
        setShowProcessCrypto(false);
        setCryptoContribution({
          amount: '',
          currency: 'USDT',
          network: 'polygon',
          transaction_hash: '',
          sender_wallet: ''
        });
        fetchContributions();
        if (onContributionUpdate) onContributionUpdate();
      } else {
        showFeedback('error', data.detail || 'Erreur lors du traitement');
      }
    } catch (error) {
      console.error('Error processing crypto contribution:', error);
      showFeedback('error', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContributionStatus = async () => {
    if (!statusUpdate.new_status) {
      showFeedback('error', 'Veuillez sélectionner un nouveau statut');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/contributions/cash/${selectedPayment.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_id: selectedPayment.id,
          new_status: statusUpdate.new_status,
          reason: statusUpdate.reason
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        showFeedback('success', 'Statut mis à jour avec succès');
        setShowStatusUpdate(false);
        setSelectedPayment(null);
        setStatusUpdate({ new_status: '', reason: '' });
        fetchContributions();
        if (onContributionUpdate) onContributionUpdate();
      } else {
        showFeedback('error', data.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showFeedback('error', 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleExportContributions = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.payment_mode) params.append('payment_mode', filters.payment_mode);
      
      const response = await fetch(`${API}/contributions/${group.id}/export?format=${format}&${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contributions_${group.id}_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showFeedback('success', `Export ${format.toUpperCase()} téléchargé`);
      } else {
        showFeedback('error', 'Erreur lors de l\'export');
      }
    } catch (error) {
      console.error('Error exporting:', error);
      showFeedback('error', 'Erreur de connexion');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'En attente' },
      validated: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Validé' },
      confirmed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle, text: 'Confirmé' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejeté' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <IconComponent className="w-3 h-3" />
        {config.text}
      </Badge>
    );
  };

  const getPaymentModeBadge = (mode) => {
    if (mode === 'cash') {
      return (
        <Badge className="bg-orange-100 text-orange-800 flex items-center gap-1">
          <DollarSign className="w-3 h-3" />
          Espèces
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-purple-100 text-purple-800 flex items-center gap-1">
          <Coins className="w-3 h-3" />
          Crypto
        </Badge>
      );
    }
  };

  const isAdmin = user?.id === group?.admin_id || user?.role === 'super_admin';

  if (!isAdmin && user?.id && !group?.members?.includes(user.id)) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Vous n'avez pas accès à cette section.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Feedback */}
      {feedback.visible && (
        <div className={`p-4 rounded-lg ${
          feedback.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 
          'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {typeof feedback.message === 'string' ? feedback.message : JSON.stringify(feedback.message)}
        </div>
      )}

      {/* Header with Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Contributions</h2>
          <p className="text-gray-600">Gérez les contributions cash et crypto du groupe</p>
        </div>

        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Dialog open={showRecordCash} onOpenChange={setShowRecordCash}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Enregistrer Cash
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Enregistrer Contribution Cash</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="member">Membre *</Label>
                    <Select value={cashContribution.member_id} onValueChange={(value) => 
                      setCashContribution(prev => ({ ...prev, member_id: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un membre" />
                      </SelectTrigger>
                      <SelectContent>
                        {groupMembers.map(member => (
                          <SelectItem key={member.id} value={member.id}>
                            {member.username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="amount">Montant *</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={cashContribution.amount}
                      onChange={(e) => setCashContribution(prev => ({ ...prev, amount: e.target.value }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="currency">Devise</Label>
                    <Select value={cashContribution.currency} onValueChange={(value) => 
                      setCashContribution(prev => ({ ...prev, currency: value }))
                    }>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="KES">KES</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="cycle">Numéro de Cycle</Label>
                    <Input
                      type="number"
                      value={cashContribution.cycle_number}
                      onChange={(e) => setCashContribution(prev => ({ ...prev, cycle_number: parseInt(e.target.value) || 1 }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes Admin</Label>
                    <Textarea
                      placeholder="Notes optionnelles..."
                      value={cashContribution.admin_notes}
                      onChange={(e) => setCashContribution(prev => ({ ...prev, admin_notes: e.target.value }))}
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={handleRecordCashContribution}
                      disabled={loading}
                      className="flex-1"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setShowRecordCash(false)}
                      disabled={loading}
                    >
                      Annuler
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Button 
              onClick={() => handleExportContributions('csv')}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Mode de Paiement</Label>
              <Select value={filters.payment_mode || "all"} onValueChange={(value) => 
                setFilters(prev => ({ ...prev, payment_mode: value === "all" ? "" : value }))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les modes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les modes</SelectItem>
                  <SelectItem value="cash">Espèces</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Membre</Label>
              <Select value={filters.member_id || "all"} onValueChange={(value) => 
                setFilters(prev => ({ ...prev, member_id: value === "all" ? "" : value }))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les membres" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les membres</SelectItem>
                  {groupMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.username}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Statut</Label>
              <Select value={filters.status || "all"} onValueChange={(value) => 
                setFilters(prev => ({ ...prev, status: value === "all" ? "" : value }))
              }>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="validated">Validé</SelectItem>
                  <SelectItem value="confirmed">Confirmé</SelectItem>
                  <SelectItem value="rejected">Rejeté</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contributions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Liste des Contributions ({contributions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : contributions.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune contribution trouvée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-medium">Membre</th>
                    <th className="text-left p-3 font-medium">Montant</th>
                    <th className="text-left p-3 font-medium">Mode</th>
                    <th className="text-left p-3 font-medium">Statut</th>
                    <th className="text-left p-3 font-medium">Date</th>
                    {isAdmin && <th className="text-left p-3 font-medium">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {contributions.map((contribution) => (
                    <tr key={contribution.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          {contribution.member_name || 'Utilisateur inconnu'}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="font-medium">
                          {contribution.amount} {contribution.currency}
                        </div>
                        <div className="text-sm text-gray-500">
                          Cycle {contribution.cycle_number}, Tour {contribution.payment_round}
                        </div>
                      </td>
                      <td className="p-3">
                        {getPaymentModeBadge(contribution.payment_mode)}
                      </td>
                      <td className="p-3">
                        {getStatusBadge(contribution.status)}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(contribution.payment_date).toLocaleDateString('fr-FR')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(contribution.payment_date).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="p-3">
                          <div className="flex gap-1">
                            {contribution.payment_mode === 'cash' && contribution.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedPayment(contribution);
                                  setStatusUpdate({ new_status: '', reason: '' });
                                  setShowStatusUpdate(true);
                                }}
                              >
                                <Edit3 className="w-3 h-3" />
                              </Button>
                            )}
                            {contribution.blockchain_hash && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  // Open blockchain explorer
                                  const explorerUrls = {
                                    ethereum: 'https://etherscan.io/tx/',
                                    polygon: 'https://polygonscan.com/tx/',
                                    bsc: 'https://bscscan.com/tx/'
                                  };
                                  const url = explorerUrls[contribution.blockchain_network] || explorerUrls.ethereum;
                                  window.open(`${url}${contribution.blockchain_hash}`, '_blank');
                                }}
                                title="Voir sur blockchain explorer"
                              >
                                <Hash className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      {isAdmin && (
        <Dialog open={showStatusUpdate} onOpenChange={setShowStatusUpdate}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Mettre à jour le Statut</DialogTitle>
            </DialogHeader>
            {selectedPayment && (
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium">Contribution sélectionnée</h4>
                  <p className="text-sm text-gray-600">
                    {selectedPayment.member_name} - {selectedPayment.amount} {selectedPayment.currency}
                  </p>
                  <p className="text-xs text-gray-500">
                    Statut actuel: {getStatusBadge(selectedPayment.status)}
                  </p>
                </div>

                <div>
                  <Label htmlFor="new_status">Nouveau Statut *</Label>
                  <Select value={statusUpdate.new_status} onValueChange={(value) => 
                    setStatusUpdate(prev => ({ ...prev, new_status: value }))
                  }>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="validated">Validé</SelectItem>
                      <SelectItem value="confirmed">Confirmé</SelectItem>
                      <SelectItem value="rejected">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="reason">Motif / Notes</Label>
                  <Textarea
                    placeholder="Expliquez la raison de cette mise à jour..."
                    value={statusUpdate.reason}
                    onChange={(e) => setStatusUpdate(prev => ({ ...prev, reason: e.target.value }))}
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleUpdateContributionStatus}
                    disabled={loading}
                    className="flex-1"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Mise à jour...' : 'Mettre à jour'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowStatusUpdate(false)}
                    disabled={loading}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Member Crypto Contribution Dialog */}
      {!isAdmin && (
        <Dialog open={showProcessCrypto} onOpenChange={setShowProcessCrypto}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <Coins className="w-4 h-4 mr-2" />
              Contribuer en Crypto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Contribuer en Cryptomonnaie</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="amount">Montant *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={cryptoContribution.amount}
                  onChange={(e) => setCryptoContribution(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="currency">Cryptomonnaie</Label>
                <Select value={cryptoContribution.currency} onValueChange={(value) => 
                  setCryptoContribution(prev => ({ ...prev, currency: value }))
                }>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USDT">USDT (Tether)</SelectItem>
                    <SelectItem value="USDC">USDC (USD Coin)</SelectItem>
                    <SelectItem value="ETH">ETH (Ethereum)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="network">Réseau</Label>
                <Select value={cryptoContribution.network} onValueChange={(value) => 
                  setCryptoContribution(prev => ({ ...prev, network: value }))
                }>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="polygon">Polygon (MATIC)</SelectItem>
                    <SelectItem value="ethereum">Ethereum (ETH)</SelectItem>
                    <SelectItem value="bsc">Binance Smart Chain (BNB)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="transaction_hash">Hash de Transaction *</Label>
                <Input
                  placeholder="0x..."
                  value={cryptoContribution.transaction_hash}
                  onChange={(e) => setCryptoContribution(prev => ({ ...prev, transaction_hash: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="sender_wallet">Portefeuille Expéditeur *</Label>
                <Input
                  placeholder="0x..."
                  value={cryptoContribution.sender_wallet}
                  onChange={(e) => setCryptoContribution(prev => ({ ...prev, sender_wallet: e.target.value }))}
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleProcessCryptoContribution}
                  disabled={loading}
                  className="flex-1"
                >
                  <Coins className="w-4 h-4 mr-2" />
                  {loading ? 'Traitement...' : 'Traiter'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowProcessCrypto(false)}
                  disabled={loading}
                >
                  Annuler
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContributionManager;